import React from 'react';
import LegalLayout from '../components/LegalLayout';

export default function AIContentPolicyPage() {
  return (
    <LegalLayout title="AI Content Policy" lastUpdated="June 25, 2026" testId="ai-content-policy-page">
      <p>This policy defines what content can and cannot be created on <strong>Delphi AI</strong> and your obligations when publishing or distributing AI-generated outputs.</p>

      <h2>1. What is allowed</h2>
      <ul>
        <li>Original creative content for your own brands, characters, or projects.</li>
        <li>Stylized references inspired by genres, eras, or moods (cyberpunk, noir, editorial, fantasy, etc.).</li>
        <li>AI personas you build from your own uploaded references or wholly synthetic seeds.</li>
        <li>Marketing assets for products and services you legitimately represent.</li>
      </ul>

      <h2>2. Prohibited content</h2>
      <p>You may <strong>not</strong> use Delphi to create:</p>
      <ul>
        <li>Child sexual abuse material (CSAM) of any kind — zero tolerance, immediate ban + legal reporting.</li>
        <li>Non-consensual intimate imagery (NCII) of any real person.</li>
        <li>Deepfakes or impersonations of real, identifiable individuals without their explicit consent — including public figures, politicians, and celebrities — particularly in defamatory, sexual, political-disinformation, or fraud contexts.</li>
        <li>Content that promotes terrorism, real-world violence, self-harm, or illegal activity.</li>
        <li>Harassment, hate speech, or content targeting protected groups.</li>
        <li>Forged identification documents, currency, or official insignia.</li>
        <li>Content that infringes copyright, trademark, or trade dress you do not have rights to use.</li>
      </ul>

      <h2>3. NSFW &amp; mature content</h2>
      <p>Mature/adult content is permitted only on tiers that explicitly enable it (Pro and above), and only when:</p>
      <ul>
        <li>All depicted subjects are clearly adult (18+) and wholly synthetic, or are real consenting adults with documented authorization.</li>
        <li>No real person is depicted without explicit consent.</li>
        <li>The content does not violate any of section 2 above.</li>
      </ul>

      <h2>4. Disclosure obligations</h2>
      <p>You are responsible for complying with disclosure requirements in your jurisdiction (e.g., EU AI Act, FTC endorsement guides, platform-specific AI-content labels). Where required, you must clearly label AI-generated outputs as such, especially in political, financial, news, or product-endorsement contexts.</p>

      <h2>5. Watermarks &amp; provenance</h2>
      <p>Free tier outputs include a visible Delphi watermark. Paid outputs may include invisible provenance metadata (C2PA-compatible) so platforms and viewers can verify AI origin. Removing watermarks or stripping provenance metadata to misrepresent AI content is a violation of these Terms.</p>

      <h2>6. Voice cloning &amp; talking-photo</h2>
      <p>You may clone voices only for: (a) yourself, (b) consenting adults who have authorized you in writing, or (c) wholly synthetic personas. You may animate photos only of yourself, consenting subjects, or synthetic characters. Misuse will result in immediate account termination.</p>

      <h2>7. Moderation</h2>
      <p>We employ automated moderation (prompt + image classifiers) plus partner-provider moderation (Sora 2, Nano Banana, ElevenLabs, HeyGen each enforce their own policies). Generations may be blocked, refunded, or flagged for human review. We may also remove already-stored outputs that we determine, in good faith, violate this Policy.</p>

      <h2>8. Reporting</h2>
      <p>To report misuse, CSAM, NCII, or copyright infringement, email <strong>abuse@delphiai.ca</strong>. CSAM reports are escalated to NCMEC and relevant law enforcement immediately.</p>

      <h2>9. Account consequences</h2>
      <ul>
        <li>First-time minor violations: content removed + warning.</li>
        <li>Repeated violations or any section-2 prohibition: account suspension or permanent termination, no refund of unused credits/subscription time.</li>
        <li>Severe violations (CSAM, fraud, mass NCII): immediate permanent ban + legal reporting.</li>
      </ul>

      <h2>10. Updates</h2>
      <p>This policy evolves with the technology. Material changes will be communicated by email or in-product notice.</p>
    </LegalLayout>
  );
}
